// What the core-update banner should say, as a pure function (ADR 0178).
//
// The decision lives here rather than inside the component for the reason the
// reveal's staging logic does: this package runs its unit tests under Node with
// no DOM, so anything worth pinning has to be a plain module. And this is worth
// pinning — the rule it encodes is the one that was got wrong in production.
//
// THE RULE: a 202 from the install route is not evidence. It means the frame
// reached a socket. The boat can refuse it outright, or accept it and fail
// fifteen minutes later, which is exactly what happened on 2026-08-14 while the
// page said "Update approved — the boat installs and restarts on its own" the
// whole time. So every line comes from what the BOAT reported, and a stored
// outcome counts as THIS attempt's only when its `commandKey` matches the
// `idempotencyKey` the 202 handed back.

import type { Boat } from './useBoats';

import { isCoreOutdated } from './coreVersion';

/**
 * How long to keep waiting for a boat that has gone quiet. Its own staleness
 * bound is 15 minutes and the terminal beat rides an outbox that drains on
 * reconnect, so 20 gives the slowest honest answer room to arrive. After that
 * the banner says nobody answered — never that it worked.
 */
export const WAIT_LIMIT_MS = 20 * 60_000;

/**
 * How recent a stored outcome must be to show with NO matching attempt — the
 * page-reload case. Rendered as a past attempt, never as the current one, and
 * it never disables the button.
 */
export const RECENT_OUTCOME_MS = 60 * 60_000;

export type UpdateStatusTone = 'waiting' | 'failed';

export interface UpdateBannerInput {
  boatVersion: string | null;
  coreLatest: string | null;
  lastUpdate: Boat['lastUpdate'];
  /** The key the 202 returned, and when we started waiting on it. */
  attempt: { commandKey: string; startedAt: number } | null;
  /** The POST itself is in flight. */
  sending: boolean;
  /** The POST failed — the boat never heard anything. */
  error: string | null;
  /**
   * The version whose offer was closed on this browser, from
   * `updateBannerDismiss.ts`. Silences the offer only while it still names the
   * latest release — a newer one is a new offer, not the one that was declined.
   */
  dismissedVersion: string | null;
  /**
   * The boat is under update enforcement (cloud sync paused, or a version that
   * cannot update remotely). The update is no longer optional and this banner
   * holds the only remote approve button, so it cannot be put down.
   */
  enforced: boolean;
  now: number;
}

export interface UpdateBannerView {
  /** Nothing to say — render no banner at all. */
  visible: boolean;
  /** The offer line, when the boat is genuinely behind the channel. */
  offer: string | null;
  status: { text: string; tone: UpdateStatusTone } | null;
  /** Absent when there is no upgrade to offer (a failure alone still shows). */
  action: { label: string; disabled: boolean } | null;
  /** Whether to render the close button — a bare offer, on a boat free to ignore it. */
  dismissible: boolean;
  /** True once the boat has answered this attempt terminally. */
  settled: boolean;
  /** True when this attempt has been waiting past the limit. */
  timedOut: boolean;
}

const NOTHING: UpdateBannerView = {
  visible: false,
  offer: null,
  status: null,
  action: null,
  dismissible: false,
  settled: false,
  timedOut: false,
};

function isTerminal(phase: string): boolean {
  return phase === 'rejected' || phase === 'failed' || phase === 'complete';
}

export function updateBannerView(input: UpdateBannerInput): UpdateBannerView {
  const { boatVersion, coreLatest, lastUpdate, attempt, sending, error, now } = input;
  const { dismissedVersion, enforced } = input;
  const outdated = coreLatest !== null && isCoreOutdated(boatVersion, coreLatest);
  const mine =
    attempt !== null && lastUpdate !== null && lastUpdate.commandKey === attempt.commandKey;
  const settled = mine && isTerminal(lastUpdate.phase);
  const timedOut = attempt !== null && !settled && now - attempt.startedAt > WAIT_LIMIT_MS;

  // Our own approval completed: the version line has already flipped and there
  // is nothing left to say.
  if (mine && lastUpdate.phase === 'complete') return NOTHING;
  if (!outdated && attempt === null && error === null) {
    // Still worth showing a recent failure on a boat that is now up to date?
    // No — it either succeeded since or is no longer actionable.
    return NOTHING;
  }

  let status: { text: string; tone: UpdateStatusTone } | null = null;
  if (error !== null) {
    status = { text: error, tone: 'failed' };
  } else if (mine && lastUpdate.phase === 'accepted') {
    status = {
      text: `Installing v${lastUpdate.version ?? coreLatest ?? ''} — the boat restarts on its own.`,
      tone: 'waiting',
    };
  } else if (mine && isTerminal(lastUpdate.phase)) {
    status = {
      text: lastUpdate.detail
        ? `The update didn't start: ${lastUpdate.detail}`
        : "The update didn't start, and the boat didn't say why.",
      tone: 'failed',
    };
  } else if (timedOut) {
    status = {
      text: 'No answer from the boat. Nothing has been installed — try again when it is back online.',
      tone: 'failed',
    };
  } else if (sending || attempt !== null) {
    status = { text: 'Sent to your boat — waiting for it to answer…', tone: 'waiting' };
  } else if (
    lastUpdate !== null &&
    lastUpdate.phase !== 'complete' &&
    isTerminal(lastUpdate.phase) &&
    lastUpdate.at !== null
  ) {
    // No approval of ours in flight, but the boat has a recent failure on
    // record — the case after a page reload. Say whose it is, and leave the
    // button alone.
    const at = new Date(lastUpdate.at).getTime();
    if (Number.isFinite(at) && now - at < RECENT_OUTCOME_MS && now - at >= 0) {
      status = {
        text: lastUpdate.detail
          ? `The last attempt didn't start: ${lastUpdate.detail}`
          : "The last attempt didn't start.",
        tone: 'failed',
      };
    }
  }

  // A closed offer, and nothing has happened since. Only a BARE offer can be
  // dismissed: once there is a status line the banner is answering something the
  // owner clicked — an install in progress, a failure, a boat that never replied
  // — and none of that is a nag to be put down.
  // An enforced boat is not covered by a dismissal taken before enforcement
  // began: the offer stopped being optional, so the old "not now" lapses.
  const closed =
    !enforced && status === null && dismissedVersion !== null && dismissedVersion === coreLatest;
  if (closed) return NOTHING;

  // Only an outstanding approval of ours holds the button down. A failure, a
  // timeout, or somebody else's stale outcome all leave it usable.
  const waiting = sending || (attempt !== null && !settled && !timedOut);

  return {
    visible: outdated || status !== null,
    dismissible: outdated && status === null && !enforced,
    offer: outdated ? `Update available — v${coreLatest ?? ''}` : null,
    status,
    action: outdated
      ? {
          label: waiting ? 'Approving…' : `Approve update to v${coreLatest ?? ''}`,
          disabled: waiting,
        }
      : null,
    settled,
    timedOut,
  };
}
